// src/views/proprio/ProprioMessagesView.jsx — Permissions 42 Phase 2 (espace proprio)
//
// Inbox voyageur en LECTURE SEULE, MÊME ORGANISATION que l'inbox admin :
//   liste de conversations groupées par logement (gauche) + fil sélectionné
//   (droite) rendu par le composant admin réutilisé `InboxThread` en mode
//   `readOnly` (composer d'envoi masqué — la réponse reste une action admin).
//
// Co-hôte (ADR-205) : la PII voyageur (nom, email, contenu) est légitime pour le
// proprio-titulaire ; le cloisonnement DUR aux logIds est côté serveur
// (proprio_messages / proprio_calendar). Aucune écriture, aucun « non lu »
// (read_at = concept opérationnel admin, hors whitelist proprio). Escaping React
// natif (ThreadDisplay) → pas d'injection HTML.

import { useState, useEffect, useCallback, useMemo } from 'react'
import { MessageSquare, Mail, MessageCircle, Inbox, Home, ChevronLeft } from 'lucide-react'
import { DS } from '../../lib/ui'
import { useIsMobile } from '../../lib/useIsMobile'
import InboxThread from '../../tabs/messages/InboxThread'
import { fetchProprioMessages, fetchProprioCalendar, fetchProprioLogements } from '../../lib/proprio-api'
import { groupThreadsByLastMessage, filterThreadsByLogement } from '../../lib/proprio-messages-sort'
// Micro-lot admin (2026-08-01) — même dérivation d'extrait que l'inbox admin :
// HTML OTA brut nettoyé À L'AFFICHAGE seulement (body stocké intact).
import { deriveInboxPreview } from '../../lib/inbox-preview'
import { SectionTitle, Loading, ErrorBox, EmptyBox, ReadOnlyHint } from './_proprioUi'

// Plage large pour l'enrichissement résa (nom complet / dates / source dans
// l'en-tête du fil). Réutilise l'endpoint cloisonné proprio_calendar — pas de
// nouveau backend. Bornes : 2 ans en arrière → fin d'année prochaine.
function wideRange() {
  const y = new Date().getFullYear()
  return { from: `${y - 2}-01-01`, to: `${y + 1}-12-31` }
}

// Icône de canal (lucide trait fin) — remplace l'emoji. whatsapp en vert, les
// autres en marine ; rendu dans une pastille.
function CanalIcon({ canal, size = 12 }) {
  if (canal === 'email') return <Mail size={size} strokeWidth={1.8} color={DS.marine} />
  if (canal === 'tchat_ota') return <MessageSquare size={size} strokeWidth={1.8} color={DS.marine} />
  if (canal === 'whatsapp') return <MessageCircle size={size} strokeWidth={1.8} color={DS.success} />
  return <Inbox size={size} strokeWidth={1.8} color={DS.marine} />
}

function shortTime(ts) {
  if (!ts) return '—'
  const d = new Date(ts)
  const now = new Date()
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  }
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })
}

export default function ProprioMessagesView() {
  const isMobile = useIsMobile()
  const [state, setState] = useState({ loading: true, error: null, messages: [], reservations: {}, logements: [] })
  const [selectedThreadId, setSelectedThreadId] = useState(null)
  // Mobile : navigation liste ⟷ fil (swap dur, pas de 2 colonnes < 768).
  // `false` = liste affichée ; passe à `true` au clic d'une conversation.
  const [mobileShowThread, setMobileShowThread] = useState(false)
  // Filtre logement optionnel par-dessus le tri chronologique. « all » = aucun
  // filtre (défaut) ; ne change JAMAIS l'ordre des fils restants.
  const [logFilter, setLogFilter] = useState('all')

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }))
    try {
      const { from, to } = wideRange()
      const [msgRes, calRes, logRes] = await Promise.all([
        fetchProprioMessages({}),
        fetchProprioCalendar({ from, to }),
        fetchProprioLogements(),
      ])
      // Map résa id → résa (enrichissement en-tête/lignes ; même clé que
      // inbox_messages.reservation_id, cf. inbox admin).
      const reservations = {}
      for (const r of calRes.reservations || []) reservations[r.id] = r
      const logements = (logRes.logements || []).map((l) => ({ id: l.id, nom: l.nom }))
      setState({ loading: false, error: null, messages: msgRes.messages || [], reservations, logements })
    } catch (e) {
      setState({ loading: false, error: e.message, messages: [], reservations: {}, logements: [] })
    }
  }, [])

  useEffect(() => { load() }, [load])

  // Groupe les messages par fil + trie « dernier message en haut » (helper pur,
  // testé). Tri par défaut chronologique décroissant — plus de regroupement par
  // logement (retour terrain #2). Pas de unreadCount (lecture seule).
  // Lot « conversation enrichie » + « SMS dans le fil » + système d'avis
  // « option a » — CEINTURE défense en profondeur (le serveur fait foi :
  // proprio_messages masque déjà canal='form_comment', 'sms' ET
  // 'depart_feedback' côté lib/proprio-handlers.js) : les commentaires du
  // formulaire de bienvenue, les traces de SMS ET les ressentis négatifs de
  // fin de séjour sont réservés à l'admin, jamais rendus dans l'espace proprio.
  const threads = useMemo(
    () =>
      groupThreadsByLastMessage(
        (state.messages || []).filter(
          (m) =>
            m?.canal !== 'form_comment' &&
            m?.canal !== 'sms' &&
            m?.canal !== 'depart_feedback',
        ),
      ),
    [state.messages],
  )

  // Résolution du logement d'un thread (priorité lastMsg.logement_id, fallback
  // résa) — même logique que l'inbox admin.
  const threadLogId = useCallback((t) => {
    const lastMsg = t.messages[t.messages.length - 1]
    const resa = lastMsg?.reservation_id ? state.reservations[lastMsg.reservation_id] : null
    return lastMsg?.logement_id || resa?.logement_id || null
  }, [state.reservations])

  // Libellé logement d'un fil (pour la pastille de chaque ligne, le regroupement
  // visuel par section ayant disparu).
  const threadLogLabel = useCallback((t) => {
    const logId = threadLogId(t)
    const log = logId ? state.logements.find((l) => l.id === logId) : null
    return log ? `${log.id} · ${log.nom}` : '(logement inconnu)'
  }, [threadLogId, state.logements])

  // Options du filtre logement : uniquement les logements RÉELLEMENT présents
  // dans les fils (triés par libellé), « (logement inconnu) » en dernier.
  const logOptions = useMemo(() => {
    const seen = new Map()
    let hasUnknown = false
    for (const t of threads) {
      const logId = threadLogId(t)
      if (!logId) { hasUnknown = true; continue }
      if (!seen.has(logId)) {
        const log = state.logements.find((l) => l.id === logId)
        seen.set(logId, log ? `${log.id} · ${log.nom}` : String(logId))
      }
    }
    const opts = Array.from(seen.entries()).map(([value, label]) => ({ value, label }))
    opts.sort((a, b) => a.label.localeCompare(b.label))
    if (hasUnknown) opts.push({ value: '__unknown__', label: '(logement inconnu)' })
    return opts
  }, [threads, state.logements, threadLogId])

  // Fils visibles = tri chronologique, restreint au filtre logement éventuel.
  const visibleThreads = useMemo(
    () => filterThreadsByLogement(threads, logFilter, threadLogId),
    [threads, logFilter, threadLogId],
  )

  // Auto-sélection du 1er fil VISIBLE (le plus récent) si rien de sélectionné ou
  // si le fil sélectionné sort du filtre courant.
  useEffect(() => {
    if (visibleThreads.length === 0) return
    if (!visibleThreads.some((t) => t.thread_id === selectedThreadId)) {
      setSelectedThreadId(visibleThreads[0].thread_id)
    }
  }, [selectedThreadId, visibleThreads])

  const selected = threads.find((t) => t.thread_id === selectedThreadId)

  // Barre de filtre logement (au-dessus de la liste). Sélecteur natif : lisible et
  // empilable < 375px. « Tous les logements » par défaut = aucun filtre.
  const filterBar = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: DS.pageBg, borderBottom: `1px solid ${DS.border}` }}>
      <Home size={13} strokeWidth={1.8} color={DS.marine} />
      <label htmlFor="proprio-msg-log-filter" style={{ fontSize: 10.5, fontWeight: 600, color: DS.textMuted, letterSpacing: 0.2, whiteSpace: 'nowrap' }}>Logement</label>
      <select id="proprio-msg-log-filter" value={logFilter} onChange={(e) => setLogFilter(e.target.value)}
        style={{ flex: 1, minWidth: 0, fontFamily: DS.fontBody, fontSize: 12, fontWeight: 600, color: DS.marine, background: DS.card, border: `0.5px solid ${DS.borderHover}`, borderRadius: DS.radiusControl, padding: '6px 8px', cursor: 'pointer' }}>
        <option value="all">Tous les logements</option>
        {logOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  )

  // Liste des conversations, triées « dernier message en haut » (plus de
  // regroupement par logement) ; chaque ligne porte une pastille logement.
  const listPanel = (
    <div style={{ background: DS.card, borderRadius: DS.radiusCard, border: `0.5px solid ${DS.border}`, boxShadow: '0 1px 3px rgba(33,83,135,.05)', overflow: 'hidden', maxHeight: isMobile ? 'none' : 'calc(100vh - 260px)', overflowY: 'auto' }}>
      <div style={{ position: 'sticky', top: 0, zIndex: 1 }}>{filterBar}</div>
      {visibleThreads.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: DS.textMuted, fontSize: 12 }}>Aucune conversation pour ce logement.</div>
      ) : visibleThreads.map((t) => {
        const lastMsg = t.messages[t.messages.length - 1]
        const resa = lastMsg?.reservation_id ? state.reservations[lastMsg.reservation_id] : null
        const isSel = t.thread_id === selectedThreadId
        const who = resa
          ? `${resa.guest_first_name || '?'} ${resa.guest_last_name || ''}`.trim()
          : (lastMsg?.sender_name || lastMsg?.sender_email || 'Voyageur')
        return (
          <button key={t.thread_id} onClick={() => { setSelectedThreadId(t.thread_id); setMobileShowThread(true) }} className="fbz-rowhover"
            style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer', background: isSel ? DS.cielSoft : DS.card, padding: '10px 12px', borderBottom: `1px solid ${DS.border}`, borderLeft: isSel ? `3px solid ${DS.corail}` : '3px solid transparent', fontFamily: DS.fontBody }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 6 }}>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: DS.marine }}>{who}</span>
              <span style={{ fontSize: 10, color: DS.textMuted }}>{shortTime(lastMsg?.created_at)}</span>
            </div>
            <div style={{ fontSize: 10, color: DS.textMuted, marginTop: 2, display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 5px', borderRadius: 5, background: DS.cielSoft }}><CanalIcon canal={lastMsg?.canal} /></span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}><Home size={10} strokeWidth={1.8} color={DS.textMuted} />{threadLogLabel(t)}</span>
              {resa?.source && <span>· {resa.source}</span>}
            </div>
            <div style={{ fontSize: 11, color: DS.textSecondary, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {lastMsg?.subject && <span style={{ fontWeight: 600 }}>{lastMsg.subject} · </span>}
              {deriveInboxPreview(lastMsg?.body)}
            </div>
          </button>
        )
      })}
    </div>
  )

  // Fil sélectionné — composant admin InboxThread réutilisé EN LECTURE SEULE.
  const threadPanel = (
    <div style={{ background: DS.card, borderRadius: DS.radiusCard, border: `0.5px solid ${DS.border}`, boxShadow: '0 1px 3px rgba(33,83,135,.05)', overflow: 'hidden', minHeight: 480 }}>
      {selected
        ? <InboxThread key={selected.thread_id} thread={selected} reservations={state.reservations} logements={state.logements} readOnly />
        : <div style={{ padding: 32, textAlign: 'center', color: DS.textMuted, fontSize: 13 }}>Sélectionnez une conversation à gauche.</div>}
    </div>
  )

  let body = null
  if (!state.loading && !state.error && threads.length > 0) {
    if (isMobile) {
      // Swap dur : la liste OU le fil (avec retour), jamais 2 colonnes serrées.
      body = (mobileShowThread && selected) ? (
        <div>
          <button type="button" onClick={() => setMobileShowThread(false)} className="fbz-tap fbz-press"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: DS.card, border: `0.5px solid ${DS.borderHover}`, borderRadius: DS.radiusControl, padding: '8px 14px', color: DS.marine, fontWeight: 600, fontSize: 13, cursor: 'pointer', marginBottom: 10, fontFamily: DS.fontBody }}>
            <ChevronLeft size={16} strokeWidth={2} /> Conversations
          </button>
          {threadPanel}
        </div>
      ) : listPanel
    } else {
      body = (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 300px) minmax(0, 1fr)', gap: 14, alignItems: 'start' }}>
          {listPanel}
          {threadPanel}
        </div>
      )
    }
  }

  return (
    <div>
      <SectionTitle icon={MessageSquare} sub="Échanges avec les voyageurs de vos logements (consultation).">Messages</SectionTitle>
      <ReadOnlyHint />

      {state.loading && <Loading />}
      {!state.loading && state.error && <ErrorBox message={state.error} onRetry={load} />}

      {!state.loading && !state.error && threads.length === 0 && <EmptyBox>Aucun message.</EmptyBox>}

      {body}
    </div>
  )
}
